import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  Divider,
} from '@mui/material';
import { PopupDialog, LookupListItem, LookupRadio } from '../../../../components/common';
import { type ArticuloFiltered } from '../types/articulo';
import { useArticuloStore } from '../store';

interface ArticuloSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (idArticulo: number) => void;
  title?: string;
}

const ArticuloSelectionModal: React.FC<ArticuloSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Seleccionar Artículo'
}) => {
  const [selectedArticulo, setSelectedArticulo] = useState<ArticuloFiltered | null>(null);

  const {
    filteredArticulos,
    loading,
    error,
    fetchFilteredArticulos,
    clearError
  } = useArticuloStore();

  useEffect(() => {
    if (open) {
      fetchFilteredArticulos();
      setSelectedArticulo(null);
    }
  }, [open, fetchFilteredArticulos]);

  const handleSelect = () => {
    if (selectedArticulo) {
      onSelect(selectedArticulo.idArticulo);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedArticulo(null);
    onClose();
  };

  const handleArticuloClick = (articulo: ArticuloFiltered) => {
    setSelectedArticulo(articulo);
  };

  const handleCloseError = () => {
    clearError();
  };

  return (
    <PopupDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="lg"
      primaryAction={{
        label: 'Seleccionar',
        onClick: handleSelect,
        disabled: !selectedArticulo || loading,
        loading: loading,
        variant: 'contained',
        color: 'primary'
      }}
      secondaryAction={{
        label: 'Cancelar',
        onClick: handleClose,
        variant: 'outlined',
        color: 'inherit'
      }}
    >
      {error && (
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ minHeight: 300, maxHeight: 500, overflow: 'auto' }}>
          {filteredArticulos.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No hay artículos disponibles
            </Typography>
          ) : (
            <List>
              {filteredArticulos.map((articulo: ArticuloFiltered, index: number) => (
                <React.Fragment key={articulo.idArticulo}>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ p: 0 }}
                      onClick={() => handleArticuloClick(articulo)}
                      selected={selectedArticulo?.idArticulo === articulo.idArticulo}
                    >
                      <LookupRadio
                        checked={selectedArticulo?.idArticulo === articulo.idArticulo}
                        value={articulo.idArticulo}
                      />
                      <LookupListItem
                        mainText={`${articulo.codigoArticulo} - ${articulo.descripcion}`}
                        secondaryText={`Especie: ${articulo.especieNombreComun}`}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredArticulos.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}
    </PopupDialog>
  );
};

export default ArticuloSelectionModal;